/**
 * LandingPageV5 — "The Home for Black Business"
 *
 * Redesign that lifts the site from a directory into a movement landing page.
 * Key sections (top → bottom):
 *   1. Hero  — navy + gold, US "dot map" motif, search + dual CTAs
 *   2. Businesses of the Week (rotating carousel of curated founders)
 *   3. Spotlight Businesses (horizontal rail)
 *   4. Stats strip (live data)
 *   5. Explore by Category (7 pastel tiles — cream / blue-grey / muted gold)
 *   6. Trending Now (auto-mixed from /api/trending/mixed)
 *   7. Trust bar (gold icons)
 *   8. Support. Discover. Elevate. (photo + dual CTA)
 *   9. Built for Business Owners
 *  10. How It Works (3 steps)
 *  11. Founding Business testimonials
 *  12. Footer (shared component)
 *
 * Palette stays **blue + gold + cream** — no straight black backgrounds.
 * Every dark section uses `bg-tbc-navy` (deep blue), never pure #000.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VersionSwitcher from "@/components/VersionSwitcher";
import BusinessLogo from "@/components/BusinessLogo";
import TierBadge from "@/components/TierBadge";
import SubmitBusinessDialog from "@/components/SubmitBusinessDialog";
import StateCoverageMap from "@/components/StateCoverageMap";
import AnimatedNumber from "@/components/AnimatedNumber";
import LatestDropsRail from "@/components/LatestDropsRail";
import FoundingHomepageTeaser from "@/components/FoundingHomepageTeaser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { resolveLogoUrl } from "@/lib/assetUrl";
import {
  Search, Star, ArrowRight, MapPin, Sparkles, Users, Building2,
  BadgeCheck, ShieldCheck, HeartHandshake, TrendingUp, Utensils,
  Scissors, Scale, Home as HomeIcon, Music2, Cpu, GraduationCap,
  ChevronLeft, ChevronRight, Flame, Newspaper, Sofa,
} from "lucide-react";

// -----------------------------------------------------------------------------
// City-twinkle overlay — sits on top of the hero USA PNG and renders gold
// dots at major-city positions with a staggered pulse animation, but ONLY
// for cities that already have a verified TBC business. Turns the map into
// a live coverage indicator: no verified businesses in a city → its dot
// stays dark. Fetches from `/api/businesses/cities?verified_only=true`.
const CityTwinkleOverlay = () => {
  // Master list of positioned cities. Match on lowercased city name against
  // the API response. Only entries with a match will render as gold dots.
  const CITY_POSITIONS = [
    // [key, x, y, delayBucket] — key matched case-insensitively vs city name
    ["seattle",       82,  62, 0],
    ["portland",      84,  82, 3],
    ["san francisco", 60, 130, 5],
    ["oakland",       62, 132, 2],
    ["san jose",      65, 138, 6],
    ["los angeles",   78, 168, 1],
    ["san diego",     90, 190, 4],
    ["las vegas",    110, 138, 6],
    ["phoenix",      138, 180, 2],
    ["albuquerque",  155, 148, 7],
    ["salt lake city", 138, 108, 0],
    ["denver",       172, 120, 4],
    ["oklahoma city", 200, 152, 1],
    ["dallas",       204, 178, 5],
    ["fort worth",   200, 178, 3],
    ["houston",      222, 200, 3],
    ["austin",       212, 190, 7],
    ["san antonio",  205, 200, 5],
    ["kansas city",  214, 138, 6],
    ["st. louis",    228, 132, 2],
    ["saint louis",  228, 132, 2],
    ["minneapolis",  206,  82, 2],
    ["milwaukee",    232,  92, 4],
    ["chicago",      244, 108, 0],
    ["detroit",      265,  96, 3],
    ["cleveland",    276, 100, 6],
    ["indianapolis", 258, 128, 5],
    ["columbus",     278, 132, 7],
    ["cincinnati",   274, 138, 1],
    ["louisville",   270, 142, 3],
    ["memphis",      252, 160, 1],
    ["nashville",    270, 158, 4],
    ["birmingham",   280, 178, 6],
    ["atlanta",      292, 176, 2],
    ["new orleans",  246, 210, 0],
    ["tampa",        320, 208, 4],
    ["orlando",      332, 216, 6],
    ["miami",        340, 232, 1],
    ["jacksonville", 308, 172, 3],
    ["charlotte",    310, 148, 5],
    ["raleigh",      326, 128, 7],
    ["washington",   330, 108, 2],
    ["dc",           330, 108, 2],
    ["baltimore",    340, 100, 4],
    ["philadelphia", 346,  90, 6],
    ["new york",     352,  82, 0],
    ["brooklyn",     352,  82, 0],
    ["boston",       368,  68, 3],
    ["richmond",     328, 128, 1],
    ["largo",        332, 108, 5], // MD — LoveMyLargo cohort
  ];

  const [litKeys, setLitKeys] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/businesses/cities?verified_only=true`)
      .then((r) => {
        if (cancelled) return;
        const set = new Set();
        for (const row of r.data?.cities || []) {
          if (row.city) set.add(row.city.trim().toLowerCase());
        }
        setLitKeys(set);
      })
      .catch(() => {
        // If the endpoint fails we fall back to lighting every city so the
        // map still feels alive.
        if (!cancelled) setLitKeys(null);
      });
    return () => { cancelled = true; };
  }, []);

  const cities = litKeys
    ? CITY_POSITIONS.filter(([k]) => litKeys.has(k))
    : CITY_POSITIONS; // pre-fetch state or fallback → show all

  return (
    <svg
      viewBox="0 0 400 260"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
      data-testid="hero-city-twinkle-overlay"
    >
      <defs>
        <style>{`
          @keyframes tbc-city-twinkle {
            0%, 100% { opacity: 0.35; transform: scale(0.85); }
            50%      { opacity: 1;    transform: scale(1.5); }
          }
          .tbc-city-dot {
            transform-box: fill-box;
            transform-origin: center;
            animation: tbc-city-twinkle 2.6s ease-in-out infinite;
            filter: drop-shadow(0 0 2px #F5C518) drop-shadow(0 0 6px rgba(245,197,24,0.55));
          }
          .tbc-city-d0 { animation-delay: 0s;   }
          .tbc-city-d1 { animation-delay: 0.3s; }
          .tbc-city-d2 { animation-delay: 0.6s; }
          .tbc-city-d3 { animation-delay: 0.9s; }
          .tbc-city-d4 { animation-delay: 1.2s; }
          .tbc-city-d5 { animation-delay: 1.5s; }
          .tbc-city-d6 { animation-delay: 1.8s; }
          .tbc-city-d7 { animation-delay: 2.1s; }
          @media (prefers-reduced-motion: reduce) {
            .tbc-city-dot { animation: none; }
          }
        `}</style>
      </defs>
      {cities.map(([key, cx, cy, d], i) => (
        <circle
          key={`${key}-${i}`}
          cx={cx}
          cy={cy}
          r={2.2}
          fill="#F5C518"
          className={`tbc-city-dot tbc-city-d${d}`}
          data-testid={`hero-city-dot-${i}`}
          data-city={key}
        />
      ))}
    </svg>
  );
};

// US "dot map" — denser dot fill so the silhouette reads clearly. Matches the
// hero mockup: a lit-up US shape in gold on the navy background. Bright dots
// represent major cities and twinkle with a staggered gold pulse so the map
// feels alive.
// -----------------------------------------------------------------------------
const UsDotMap = () => (
  <svg
    viewBox="0 0 400 260"
    className="w-full h-auto"
    aria-hidden="true"
    role="presentation"
    data-testid="usa-dot-map"
  >
    <defs>
      <radialGradient id="dotGlow" cx="50%" cy="55%" r="55%">
        <stop offset="0%" stopColor="#F5C518" stopOpacity="0.55" />
        <stop offset="70%" stopColor="#F5C518" stopOpacity="0.08" />
        <stop offset="100%" stopColor="#F5C518" stopOpacity="0" />
      </radialGradient>
      {/* Twinkling city dots — one keyframe reused with 8 offset delay classes
          so the pulses cascade instead of firing in sync. Uses `prefers-reduced-motion`
          to disable animation for accessibility. */}
      <style>{`
        @keyframes tbc-city-twinkle {
          0%, 100% { opacity: 0.35; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1.35); }
        }
        .tbc-city-dot {
          transform-box: fill-box;
          transform-origin: center;
          animation: tbc-city-twinkle 2.6s ease-in-out infinite;
          filter: drop-shadow(0 0 1.5px #F5C518);
        }
        .tbc-city-d0 { animation-delay: 0s;   }
        .tbc-city-d1 { animation-delay: 0.3s; }
        .tbc-city-d2 { animation-delay: 0.6s; }
        .tbc-city-d3 { animation-delay: 0.9s; }
        .tbc-city-d4 { animation-delay: 1.2s; }
        .tbc-city-d5 { animation-delay: 1.5s; }
        .tbc-city-d6 { animation-delay: 1.8s; }
        .tbc-city-d7 { animation-delay: 2.1s; }
        @media (prefers-reduced-motion: reduce) {
          .tbc-city-dot { animation: none; }
        }
      `}</style>
    </defs>
    <rect width="400" height="260" fill="url(#dotGlow)" opacity="0.9" />
    {/* Dense dot fill — every other grid cell — inside a US-ish shape. */}
    {Array.from({ length: 26 }).map((_, y) =>
      Array.from({ length: 44 }).map((__, x) => {
        const px = x * 8.5 + 24;
        const py = y * 8 + 20;
        // Rough US silhouette mask
        const insideUs =
          px > 40 && px < 380 &&
          py > 30 && py < 220 &&
          !(px < 100 && py > 170) &&   // Gulf gap
          !(px > 300 && py > 190) &&    // Florida cut
          !(px < 60 && py < 70) &&      // Pacific NW top-left
          !(px > 350 && py < 60);       // North-east top-right
        if (!insideUs) return null;
        // Skip a scattered ~10% to give an organic feel
        const skip = (x * 13 + y * 7) % 11 === 0;
        if (skip) return null;
        const bright = (x + y) % 3 === 0;
        // Deterministic delay bucket 0-7 so neighbouring city dots twinkle
        // out-of-phase and the whole map feels alive, not synchronized.
        const delayBucket = (x * 5 + y * 3) % 8;
        return (
          <circle
            key={`${x}-${y}`}
            cx={px}
            cy={py}
            r={bright ? 1.6 : 1}
            fill="#F5C518"
            opacity={bright ? 0.95 : 0.55}
            className={bright ? `tbc-city-dot tbc-city-d${delayBucket}` : undefined}
          />
        );
      })
    )}
    {/* Skyline silhouette across the bottom-right */}
    <g opacity="0.5">
      {[
        [220, 205, 6, 30], [230, 215, 4, 20], [238, 195, 8, 40], [250, 210, 5, 25],
        [260, 190, 7, 45], [272, 205, 5, 30], [282, 215, 4, 20], [292, 200, 6, 35],
        [304, 210, 5, 25], [316, 195, 6, 40],
      ].map(([x, y, w, h], i) => (
        <rect key={`ny-mark-${x}-${y}`} x={x} y={y} width={w} height={h} fill="#F5C518" opacity="0.7" />
      ))}
    </g>
  </svg>
);

// -----------------------------------------------------------------------------
// Category tiles — mockup-faithful vibrant gradients, one per category. Icons
// are white on the coloured tile so they pop against the saturated background.
// -----------------------------------------------------------------------------
const CATEGORY_TILES = [
  { label: "Beauty & Wellness", icon: Scissors, target: "Beauty & Wellness" },
  { label: "Food & Beverage", icon: Utensils, target: "Food & Beverage" },
  { label: "Professional Services", icon: Scale, target: "Professional Services" },
  { label: "Real Estate", icon: HomeIcon, target: "Real Estate" },
  { label: "Media & Entertainment", icon: Music2, target: "Media and Entertainment" },
  { label: "Technology", icon: Cpu, target: "Technology" },
  { label: "Health & Fitness", icon: GraduationCap, target: "Health & Fitness" },
  { label: "Home & Living", icon: Sofa, target: "Home & Living" },
];

// Trust bar items — mint check circles per the mockup
const TRUST_ITEMS = [
  { icon: BadgeCheck, title: "Verified Quality", body: "Every listing reviewed by our team." },
  { icon: HeartHandshake, title: "Community-First", body: "Built by us, for us." },
  { icon: ShieldCheck, title: "Secure & Trusted", body: "Safe, reliable, and transparent." },
  { icon: TrendingUp, title: "Support & Grow", body: "Keep our community thriving." },
];

// Trending kind → chip label + colour (matches mockup card chips)
const TREND_CHIP = {
  trending_business: { label: "Business", classes: "bg-pink-100 text-pink-700" },
  newest_business: { label: "Newly Verified", classes: "bg-emerald-100 text-emerald-700" },
  article: { label: "News", classes: "bg-sky-100 text-sky-700" },
  top_rated_business: { label: "Top Rated", classes: "bg-amber-100 text-amber-800" },
  philanthropy_business: { label: "Philanthropy Pick", classes: "bg-rose-100 text-rose-700" },
};

const HOW_STEPS = [
  { n: 1, title: "Create Your Profile", body: "Add your business details and tell your story." },
  { n: 2, title: "Get Discovered", body: "People find and support your business." },
  { n: 3, title: "Grow Your Business", body: "Build relationships and grow with the community." },
];

const TESTIMONIALS = [
  { name: "Jasmine W.", body: "TheBlkCommunity helped me grow my business and connect with amazing customers." },
  { name: "Marcus T.", body: "A platform that truly supports us. This is what our community needed." },
  { name: "Tasha R.", body: "I've discovered so many great Black-owned businesses here." },
  { name: "Kevin D.", body: "More than a directory, it's a movement." },
];

// -----------------------------------------------------------------------------
// Small building blocks
// -----------------------------------------------------------------------------

const StatBlock = ({ value, label, numericValue, suffix = "", testId }) => (
  <div className="text-center px-3 py-2" data-testid={testId}>
    <p className="font-heading text-2xl sm:text-3xl text-tbc-navy">
      {typeof numericValue === "number" ? (
        <AnimatedNumber value={numericValue} suffix={suffix} />
      ) : (
        value
      )}
    </p>
    <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-gray-500 mt-1">
      {label}
    </p>
  </div>
);

const SpotlightCard = ({ business }) => (
  <Link
    to={`/business/${business.business_id}`}
    className="flex-shrink-0 w-44 rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all"
    data-testid={`spotlight-card-${business.business_id}`}
  >
    <div className="relative aspect-square bg-tbc-cream/40 flex items-center justify-center overflow-hidden">
      {/* Prefer the hero portrait (image_url) so featured owners like
          Julius Scott show the same photo everywhere they appear. Fall
          back to the logo mark, then to the auto-generated placeholder. */}
      {business.image_url ? (
        <img
          src={resolveLogoUrl(business.image_url)}
          alt={business.name}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="p-1.5 w-full h-full flex items-center justify-center">
          <BusinessLogo name={business.name} logoUrl={business.logo_url} size="xl" shape="square" />
        </div>
      )}
      <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-tbc-gold text-tbc-navy rounded font-semibold">
        Featured
      </span>
    </div>
    <div className="p-2.5">
      <h3 className="font-heading text-tbc-navy text-sm truncate">{business.name}</h3>
      <p className="text-[11px] text-gray-500 truncate">{business.category}</p>
      <p className="text-[11px] text-gray-500 truncate">
        {business.city ? `${business.city}${business.state ? `, ${business.state}` : ""}` : ""}
      </p>
      <div className="flex items-center justify-between mt-1">
        {business.average_rating ? (
          <div className="flex items-center gap-1">
            <Star className="w-3 h-3 text-tbc-gold fill-tbc-gold" />
            <span className="text-[11px] text-gray-600">
              {Number(business.average_rating).toFixed(1)}
            </span>
          </div>
        ) : (
          <span className="text-[11px] text-gray-400">New</span>
        )}
        <TierBadge business={business} size="xs" />
      </div>
    </div>
  </Link>
);

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function LandingPageV5() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [botwList, setBotwList] = useState([]);
  const [botwIdx, setBotwIdx] = useState(0);
  const [spotlight, setSpotlight] = useState([]);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [stats, setStats] = useState({ members: null, states: null, categories: null, businesses: null, verified: null, newVerified: null });
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [featured, community, activeCats, stateBreakdown, trend, curated, seedSpot] = await Promise.all([
          // Only show businesses that have an uploaded logo on the main page —
          // no initials-only cards on Spotlight / Business of the Week.
          axios.get(`${API}/businesses?has_logo=true&limit=20`),
          axios.get(`${API}/community/stats`),
          axios.get(`${API}/businesses/categories/active`),
          axios.get(`${API}/businesses/states`),
          axios.get(`${API}/trending/mixed`),
          axios.get(`${API}/curated/spotlight`),
          axios.get(`${API}/curated/seed-spotlight`),
        ]);
        if (cancelled) return;
        const items = featured.data?.businesses || [];
        // Spotlight rail = fresh shuffle of the 20 founding businesses + up to
        // 5 newer verified fillers, sourced from the seed-spotlight endpoint.
        // Falls back to the generic featured list if the endpoint fails.
        const seedRail = seedSpot.data?.businesses || [];
        setSpotlight(seedRail.length > 0 ? seedRail : items);
        // Prefer the curated "Businesses of the Week" list (7 hand-picked
        // showcase businesses). Fallback to the first has_logo=true entry so
        // the section still renders on a fresh DB with none of the curated
        // names yet imported.
        const curatedList = curated.data?.businesses || [];
        const botw = curatedList.length > 0 ? curatedList : (items[0] ? [items[0]] : []);
        setBotwList(botw);
        setBotwIdx(0);
        setStats({
          members: community.data?.total_users ?? null,
          states: (stateBreakdown.data?.states || []).length || null,
          categories: (activeCats.data?.categories || []).length || null,
          businesses: community.data?.total_businesses ?? null,
          verified: community.data?.verified_businesses ?? null,
          newVerified: community.data?.new_verified_this_week ?? null,
        });
        setTrending(trend.data?.slots || []);
      } catch {
        // silent — hero + fallbacks still render usefully
      }
    })();
    return () => { cancelled = true; };
    // Mount-once fetch — API is a module-level constant; state setters are stable.
  }, []);

  // Auto-rotate the Businesses-of-the-Week carousel every 7s.
  useEffect(() => {
    if (botwList.length <= 1) return;
    const id = setInterval(() => {
      setBotwIdx((i) => (i + 1) % botwList.length);
    }, 7000);
    return () => clearInterval(id);
  }, [botwList.length]);

  const businessOfWeek = botwList[botwIdx] || null;
  const prevBotw = () => setBotwIdx((i) => (i - 1 + botwList.length) % botwList.length);
  const nextBotw = () => setBotwIdx((i) => (i + 1) % botwList.length);

  const onSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    window.location.href = q ? `/directory?search=${encodeURIComponent(q)}` : "/directory";
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-v5">
      <Navbar />

      {/* ======================== HERO ======================== */}
      <section className="relative overflow-hidden bg-tbc-navy text-white" data-testid="hero-section">
        {/* Background: generated USA dot-map + skyline, right-aligned, faded on
            the left so text stays legible. */}
        <div
          className="absolute inset-0 bg-no-repeat bg-right bg-cover opacity-95 pointer-events-none"
          style={{ backgroundImage: "url('/hero-usmap.png')" }}
          aria-hidden="true"
        />
        {/* Static hero — the sparkling gold dots are baked into hero-usmap.png,
            so no animated overlay needed. */}
        {/* Subtle left-side dark gradient so the copy pops against the map */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, rgba(10,25,47,0.95) 0%, rgba(10,25,47,0.75) 35%, rgba(10,25,47,0.25) 60%, rgba(10,25,47,0) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            {/* Gold "Welcome" pill — matches mockup */}
            <span className="inline-block border border-tbc-gold/70 text-tbc-gold text-[10px] sm:text-xs uppercase tracking-[0.28em] px-4 py-1.5 rounded-full mb-6">
              Welcome to The Blk Community
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
              The Home for<br />
              <span className="text-tbc-gold">Black Business</span>
            </h1>
            <p className="mt-5 text-white/80 font-body max-w-lg leading-relaxed">
              Discover trusted Black-owned businesses, products, services, and
              opportunities — all in one place. Where community and commerce
              come together.
            </p>

            <form
              onSubmit={onSearch}
              className="mt-8 flex gap-2 max-w-lg"
              data-testid="hero-search-form"
            >
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search businesses or products…"
                  className="pl-10 bg-white text-tbc-navy border-0 h-11"
                  data-testid="hero-search-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy h-11 px-6"
                data-testid="hero-search-submit"
              >
                Search
              </Button>
            </form>

            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/directory">
                <Button
                  className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-6"
                  data-testid="hero-explore-btn"
                >
                  Explore Businesses
                </Button>
              </Link>
              <Link to={user ? "/dashboard" : "/join"}>
                <Button
                  variant="outline"
                  className="border-white text-white bg-transparent hover:bg-white hover:text-tbc-navy rounded-full px-6"
                  data-testid="hero-list-btn"
                >
                  List Your Business
                </Button>
              </Link>
            </div>
          </div>

          <div className="hidden lg:block">
            {/* Right column intentionally empty — the USA dot-map + skyline is
                rendered as the section background image and bleeds into this
                grid column. Keeps text left-aligned like the mockup. */}
          </div>
        </div>
      </section>

      {/* ================= STATS STRIP ================= */}
      {/* Sits right under the hero + US map so first-time visitors see the
          scale of the community before any other content. */}
      <section className="bg-tbc-cream/50 border-y border-tbc-navy/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="stats-strip">
          {/* Members counter intentionally hidden — will show once community
              reaches a decent threshold (~100+). */}
          <StatBlock
            value="50 + DC"
            label="States in Total"
            testId="stat-states"
          />
          <StatBlock
            value={stats.categories ? `${stats.categories}+` : "—"}
            numericValue={stats.categories}
            suffix="+"
            label="Categories"
            testId="stat-categories"
          />
          <StatBlock
            value={stats.businesses == null ? "—" : `${stats.businesses}`}
            numericValue={stats.businesses}
            label="Total Businesses"
            testId="stat-total-businesses"
          />
          <StatBlock
            value={stats.verified == null ? "—" : `${stats.verified}`}
            numericValue={stats.verified}
            label="Verified &amp; counting"
            testId="stat-verified"
          />
          <StatBlock
            value={stats.newVerified == null ? "—" : `${stats.newVerified}`}
            numericValue={stats.newVerified}
            label="Verified This Week"
            testId="stat-verified-this-week"
          />
        </div>
      </section>

      {/* ================= NAVY BAND (BOTW + Spotlight + Categories) ================= */}
      <div className="bg-tbc-navy" data-testid="landing-navy-band">
      {/* ================= BUSINESSES OF THE WEEK (rotating) ================= */}
      {businessOfWeek && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12" data-testid="botw-section">
          <div
            className="relative rounded-2xl overflow-hidden bg-tbc-navy shadow-xl border border-tbc-gold/20 mx-auto max-w-5xl cursor-pointer hover:shadow-2xl hover:border-tbc-gold/40 transition-all"
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/business/${businessOfWeek.business_id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/business/${businessOfWeek.business_id}`);
              }
            }}
            aria-label={`Open ${businessOfWeek.name}`}
            data-testid="botw-card-clickable"
          >
            <div className="grid lg:grid-cols-2">
              <div className="p-6 sm:p-8 lg:p-10 text-white flex flex-col justify-center">
                <span className="inline-block px-3 py-1 bg-tbc-gold text-tbc-navy text-[10px] uppercase tracking-widest rounded-full font-semibold mb-4 w-fit">
                  Businesses of the Week
                </span>
                <h2 className="font-heading text-3xl lg:text-4xl leading-tight" data-testid="botw-name">
                  {businessOfWeek.name}
                </h2>
                <div className="mt-2"><TierBadge business={businessOfWeek} size="sm" /></div>
                <p className="text-tbc-gold font-body mt-1">
                  {businessOfWeek.category}
                </p>
                {businessOfWeek.city && (
                  <p className="text-white/70 text-sm mt-2 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {businessOfWeek.city}
                    {businessOfWeek.state ? `, ${businessOfWeek.state}` : ""}
                  </p>
                )}
                <p className="text-white/80 mt-4 line-clamp-3 leading-relaxed">
                  {businessOfWeek.description}
                </p>
                {/* Visual CTA — the whole card is the actual link. Rendered
                    as a span so we don't nest interactive elements. */}
                <span
                  className="mt-6 inline-flex items-center justify-center bg-tbc-gold text-tbc-navy rounded-full px-6 py-2 font-body font-semibold w-fit group-hover:bg-tbc-gold/90 pointer-events-none"
                  data-testid="botw-view-btn"
                >
                  View Business
                </span>
              </div>
              <div className="relative bg-tbc-cream/10 h-[280px] lg:h-[360px] flex items-center justify-center overflow-hidden">
                {businessOfWeek.image_url ? (
                  // Hero portrait — cover the whole right column so the
                  // business's chosen photo (Julius Scott, etc.) is used
                  // exactly as-is, no cropping tricks in the frame.
                  <img
                    src={resolveLogoUrl(businessOfWeek.image_url)}
                    alt={businessOfWeek.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : businessOfWeek.logo_url ? (
                  <img
                    src={resolveLogoUrl(businessOfWeek.logo_url)}
                    alt={businessOfWeek.name}
                    className="max-w-full max-h-full object-contain p-6"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <BusinessLogo name={businessOfWeek.name} size="xl" shape="rounded" />
                )}
                <div className="absolute inset-0 bg-gradient-to-l from-transparent to-tbc-navy/60 lg:block hidden pointer-events-none" />
              </div>
            </div>

            {/* Carousel controls — only render when there is >1 curated business.
                stopPropagation keeps prev/next/dot clicks from firing the parent
                card navigation. */}
            {botwList.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous business"
                  onClick={(e) => { e.stopPropagation(); prevBotw(); }}
                  className="absolute top-1/2 -translate-y-1/2 left-3 w-9 h-9 rounded-full bg-white/95 hover:bg-tbc-gold text-tbc-navy shadow-md flex items-center justify-center transition-colors z-10"
                  data-testid="botw-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  aria-label="Next business"
                  onClick={(e) => { e.stopPropagation(); nextBotw(); }}
                  className="absolute top-1/2 -translate-y-1/2 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-tbc-gold text-tbc-navy shadow-md flex items-center justify-center transition-colors z-10"
                  data-testid="botw-next-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="botw-dots">
                  {botwList.map((b, i) => (
                    <button
                      key={b.business_id}
                      type="button"
                      aria-label={`Show ${b.name}`}
                      onClick={(e) => { e.stopPropagation(); setBotwIdx(i); }}
                      className={`w-2 h-2 rounded-full transition-all ${i === botwIdx ? "bg-tbc-gold w-6" : "bg-white/40 hover:bg-white/70"}`}
                      data-testid={`botw-dot-${i}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ================= FRESH DROPS THIS WEEK ================= */}
      {/* Sits between BOTW and Spotlight so casual visitors see live
          community activity right after the featured hero. Uses the dark
          navy variant to blend with the landing page's navy band, and
          the 168-hour window keeps it feeling "this week". Renders null
          when there is no fresh activity. */}
      <LatestDropsRail
        placement="landing"
        variant="dark"
        windowHours={168}
        limit={12}
        headline="Fresh Drops This Week"
        kicker="New this week from Black-owned businesses"
      />

      {/* ================= SPOTLIGHT RAIL ================= */}
        {spotlight.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-heading text-3xl text-white">
                  <span className="text-tbc-gold">Spotlight</span> Businesses
                </h2>
                <p className="text-sm text-white/70 font-body mt-1">
                  Our founding businesses & freshly verified spotlights — reshuffled every visit.
                </p>
              </div>
              <Link to="/directory" className="text-sm text-white hover:text-tbc-gold flex items-center gap-1 flex-shrink-0">
                View All <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div
              className="flex gap-4 overflow-x-auto scroll-smooth pb-2 -mx-4 px-4"
              data-testid="spotlight-rail"
            >
              {spotlight.map((b) => (
                <SpotlightCard key={b.business_id} business={b} />
              ))}
            </div>
          </section>
        )}

        {/* ================= EXPLORE BY CATEGORY (pastel tiles on navy) ================= */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-heading text-3xl text-white">
                <span className="text-tbc-gold">Explore</span> by Category
              </h2>
            </div>
            <Link to="/directory" className="text-sm text-white hover:text-tbc-gold flex items-center gap-1">
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="category-tiles">
            {CATEGORY_TILES.map((tile) => {
              const Icon = tile.icon;
              return (
                <Link
                  key={tile.label}
                  to={`/directory?category=${encodeURIComponent(tile.target)}`}
                  className="bg-gradient-to-br from-[#F5E7B7] to-[#E8CC7A] rounded-2xl p-5 flex flex-col items-center justify-center text-center gap-3 text-tbc-navy ring-1 ring-tbc-gold/40 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all aspect-square"
                  data-testid={`category-tile-${tile.target.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="w-11 h-11 rounded-full bg-tbc-navy flex items-center justify-center">
                    <Icon className="w-5 h-5 text-tbc-gold" />
                  </div>
                  <p className="text-xs sm:text-sm font-heading leading-tight">
                    {tile.label}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

      {/* ================= STATE COVERAGE MAP ================= */}
      <StateCoverageMap />

      {/* ================= TRENDING NOW ================= */}
      {trending.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-end justify-between mb-6">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-tbc-gold" />
              <h2 className="font-heading text-3xl text-white">
                Trending <span className="text-tbc-gold">Now</span>
              </h2>
            </div>
            <p className="hidden sm:block text-sm text-white/70 font-body">
              See what&rsquo;s hot in the community right now.
            </p>
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4"
            data-testid="trending-grid"
          >
            {trending.map((slot, i) => {
              const chip = TREND_CHIP[slot.kind] || { label: slot.label, classes: "bg-gray-100 text-gray-700" };
              if (slot.kind === "article" && slot.article) {
                const a = slot.article;
                // External RSS-ingested articles open in a new tab; internal
                // originals route to /news/:id which renders NewsArticlePage.
                const externalHref = a.source_url && /^https?:\/\//i.test(a.source_url) ? a.source_url : null;
                const cardClasses = "rounded-xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all block";
                const cardInner = (
                  <>
                    <div className="aspect-[16/10] bg-tbc-cream/50 overflow-hidden">
                      {a.image ? (
                        <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Newspaper className="w-8 h-8 text-tbc-gold/70" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${chip.classes}`}>
                        {chip.label}
                      </span>
                      <h3 className="font-heading text-tbc-navy mt-2 line-clamp-2">
                        {a.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{a.category}</p>
                    </div>
                  </>
                );
                if (externalHref) {
                  return (
                    <a
                      key={`slot-${i}`}
                      href={externalHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cardClasses}
                      data-testid={`trending-slot-${slot.kind}`}
                    >
                      {cardInner}
                    </a>
                  );
                }
                return (
                  <Link
                    key={`slot-${i}`}
                    to={`/news/${a.article_id || a.id || ""}`}
                    className={cardClasses}
                    data-testid={`trending-slot-${slot.kind}`}
                  >
                    {cardInner}
                  </Link>
                );
              }
              const b = slot.business;
              if (!b) return null;
              return (
                <Link
                  key={`slot-${i}`}
                  to={`/business/${b.business_id}`}
                  className="rounded-xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid={`trending-slot-${slot.kind}`}
                >
                  <div className="aspect-[16/10] bg-tbc-cream/50 overflow-hidden flex items-center justify-center">
                    {b.image_url || b.logo_url ? (
                      <img
                        src={resolveLogoUrl(b.image_url || b.logo_url)}
                        alt={b.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <BusinessLogo name={b.name} size="lg" shape="rounded" />
                    )}
                  </div>
                  <div className="p-3">
                    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${chip.classes}`}>
                      {chip.label}
                    </span>
                    <h3 className="font-heading text-tbc-navy mt-2 truncate">
                      {b.name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      {b.category}
                      {b.city ? ` · ${b.city}, ${b.state || ""}` : ""}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
      </div>

      {/* ================= FOUNDING 100 TEASER ================= */}
      {/* Compact, prestige-first teaser. Numbers pulled live from
          `/api/founder/public/summary` so this stays in sync with the
          full charter page and every other founder surface. */}
      <FoundingHomepageTeaser />

      {/* ================= TRUST BAR ================= */}
      <section className="bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="trust-bar">
          {TRUST_ITEMS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-heading text-tbc-navy text-sm">{title}</p>
                <p className="text-xs text-gray-600 font-body">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SUPPORT. DISCOVER. ELEVATE. ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-heading text-4xl sm:text-5xl text-tbc-navy leading-tight">
            Support.<br />
            <span className="text-tbc-gold">Discover.</span><br />
            Elevate.
          </h2>
          <p className="mt-4 text-gray-600 font-body leading-relaxed max-w-md">
            A powerful community where every Black-owned business and customer
            can thrive.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button
              onClick={() => setSubmitOpen(true)}
              className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-6"
              data-testid="sde-submit-business-btn"
            >
              Submit A Business
            </Button>
            <Link to={user ? "/dashboard" : "/join"}>
              <Button
                variant="outline"
                className="border-tbc-navy text-tbc-navy bg-transparent hover:bg-tbc-navy hover:text-white rounded-full px-6"
                data-testid="sde-list-btn"
              >
                List Your Business
              </Button>
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl overflow-hidden aspect-[3/4]">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/brand/couple.jpg`}
              alt="Black couple — Black-owned business community"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="rounded-xl bg-tbc-navy p-4 flex flex-col justify-center">
            <p className="font-heading text-white text-lg leading-tight">
              Join the Movement
            </p>
            <p className="text-xs text-white/70 mt-2">
              Empowering Black businesses and building generational wealth
              together.
            </p>
          </div>
          <div className="rounded-xl overflow-hidden aspect-[3/4]">
            <img
              src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&q=80&auto=format&fit=crop"
              alt="Black entrepreneur"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ================= BUILT FOR BUSINESS OWNERS ================= */}
      <section className="bg-tbc-cream/50 border-y border-tbc-navy/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-heading text-3xl text-tbc-navy">
              Built for <span className="text-tbc-gold">Business Owners</span>
            </h2>
            <p className="text-gray-600 font-body mt-3">
              Everything you need to grow, reach, and thrive — all in one
              platform made for the culture. Built for our community. By our
              community.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { icon: Users, title: "Get Discovered", body: "List your business and get found by customers who support Black ownership." },
              { icon: Building2, title: "Grow & Connect", body: "Build your brand, network with other business owners, and access resources." },
              { icon: TrendingUp, title: "Promote & Share", body: "Share your story, showcase your products and services, and grow your audience." },
              { icon: BadgeCheck, title: "Start Your Listing", body: "Join our directory and start reaching more customers today." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-tbc-navy/5 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-tbc-gold" />
                </div>
                <p className="font-heading text-tbc-navy">{title}</p>
                <p className="text-xs text-gray-600 mt-1 font-body">{body}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to={user ? "/dashboard" : "/join"}>
              <Button
                className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8"
                data-testid="bfbo-start-btn"
              >
                Start Your Free Listing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading text-3xl text-tbc-navy text-center mb-10">
          How It <span className="text-tbc-gold">Works</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {HOW_STEPS.map((s) => (
            <div key={s.n} className="text-center" data-testid={`how-step-${s.n}`}>
              <div className="w-12 h-12 mx-auto rounded-full bg-tbc-navy text-tbc-gold font-heading text-lg flex items-center justify-center mb-3">
                {s.n}
              </div>
              <p className="font-heading text-tbc-navy">{s.title}</p>
              <p className="text-xs text-gray-600 mt-1 font-body">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/about" className="text-sm text-tbc-navy hover:text-tbc-gold inline-flex items-center gap-1">
            Learn More <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* ================= FOUNDING BUSINESS + TESTIMONIALS ================= */}
      <section className="bg-tbc-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-8 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-tbc-gold text-2xl">🏆</span>
              <p className="font-heading text-2xl">
                Become a<br />
                <span className="text-tbc-gold">Founding Business</span>
              </p>
            </div>
            <p className="text-sm text-white/70 font-body mb-4">
              Be one of the first 100 businesses featured on our platform.
            </p>
            <Link to="/pricing#founding">
              <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-6" data-testid="founding-apply-btn">
                Apply Now
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-heading text-2xl text-center mb-6">
              What Our <span className="text-tbc-gold">Community</span> Says
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="testimonials-grid">
              {TESTIMONIALS.map((t) => (
                <div key={t.name} className="bg-tbc-navy/60 border border-white/10 rounded-lg p-3">
                  <div className="flex gap-0.5 mb-2 text-tbc-gold">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={`${t.name}-star-${i}`} className="w-3 h-3 fill-tbc-gold" />
                    ))}
                  </div>
                  <p className="text-xs text-white/85 leading-relaxed line-clamp-4">
                    &ldquo;{t.body}&rdquo;
                  </p>
                  <p className="text-[11px] text-tbc-gold mt-2">— {t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <VersionSwitcher />
      <SubmitBusinessDialog open={submitOpen} onOpenChange={setSubmitOpen} />
    </div>
  );
}
